import type {
    AbstractAnalyticsDispatcher,
    OverridableActionsTypes,
    OverridableBusinessEventTypes,
} from "@asurion-core/eventualize";
import type { Logger } from "@expert/logging";
import type { Location, Size } from "@expert/shared-types";

import { create } from "zustand";
import { createJSONStorage, devtools, persist, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

interface isOzmoOpenedProps {
    currentSessionId: string;
    dispatcher: AbstractAnalyticsDispatcher<OverridableBusinessEventTypes, OverridableActionsTypes>;
    isOpened: boolean;
    logger?: Logger;
    source?: string;
}
export interface OzmoStore {
    isOzmoOpened: boolean;
    setIsOzmoOpened: (props: isOzmoOpenedProps) => void;

    ozmoFrameUrl: string;
    setOzmoFrameUrl: (ozmo: string) => void;

    ozmoFrameLocation: Location;
    setOzmoFrameLocation: (location: Location) => void;

    ozmoFrameSize: Size;
    setOzmoFrameSize: (size: Size) => void;
}

export const useOzmoStore = create<OzmoStore>()(
    subscribeWithSelector(
        immer(
            persist(
                devtools(
                    (set) => ({
                        isOzmoOpened: false,
                        setIsOzmoOpened: ({
                            currentSessionId,
                            dispatcher,
                            isOpened,
                            logger,
                            source,
                        }: isOzmoOpenedProps) => {
                            set(
                                (state) => {
                                    if (isOpened) {
                                        logger?.info({ source, url: state.ozmoFrameUrl }, "Ozmo | opening ozmo");
                                        dispatcher.dispatchBusinessEvent("Ozmo_LaunchOzmo", {
                                            currentSessionId,
                                            source,
                                            url: state.ozmoFrameUrl,
                                        });
                                    } else {
                                        logger?.info("Ozmo | closing ozmo");
                                        dispatcher.dispatchBusinessEvent("Ozmo_CloseOzmo", {
                                            currentSessionId,
                                        });
                                    }
                                    state.isOzmoOpened = isOpened;
                                },
                                false,
                                "setIsOzmoOpened",
                            );
                        },
                        ozmoFrameUrl: "https://asurion-support.sds.modeaondemand.com/agents/en/",
                        setOzmoFrameUrl: (ozmo: string) => {
                            set(
                                (state) => {
                                    state.ozmoFrameUrl = ozmo;
                                },
                                false,
                                "setOzmoFrameUrl",
                            );
                        },
                        ozmoFrameLocation: { x: 100, y: 100 },
                        setOzmoFrameLocation: (location: Location) => {
                            set(
                                (state) => {
                                    state.ozmoFrameLocation = location;
                                },
                                false,
                                "setOzmoFrameLocation",
                            );
                        },
                        ozmoFrameSize: { width: 500, height: 500 },
                        setOzmoFrameSize: (size: Size) => {
                            set(
                                (state) => {
                                    state.ozmoFrameSize = size;
                                },
                                false,
                                "setOzmoFrameSize",
                            );
                        },
                    }),
                    { enabled: import.meta.env.VITE_ENV_TYPE !== "production", store: "ozmo-frame", name: "ozmo" },
                ),
                {
                    name: "ozmo-frame-storage",
                    storage: createJSONStorage(() => sessionStorage),
                    partialize: (state) => ({
                        ozmoFrameUrl: state.ozmoFrameUrl,
                        ozmoFrameLocation: state.ozmoFrameLocation,
                        ozmoFrameSize: state.ozmoFrameSize,
                    }),
                },
            ),
        ),
    ),
);
